import {
  type ClientPostChangeConfirmation,
  ClientPostTypeEnum
} from '@DigitaleDoerfer/digitale-doerfer-api';
import { useCallback } from 'react';

import { useChangeGossip } from '@/features/api/hooks/grapevine-post-events/useChangeGossip';
import { useChangeHappening } from '@/features/api/hooks/grapevine-post-events/useChangeHappening';
import { useChangeNewsItem } from '@/features/api/hooks/grapevine-post-events/useChangeNewsItem';
import { useChangeOffer } from '@/features/api/hooks/grapevine-post-events/useChangeOffer';
import { useChangeSeeking } from '@/features/api/hooks/grapevine-post-events/useChangeSeeking';
import { useChangeSpecialPost } from '@/features/api/hooks/grapevine-post-events/useChangeSpecialPost';
import { useChangeSuggestion } from '@/features/api/hooks/grapevine-post-events/useChangeSuggestion';
import type { useMediaPlaceholders } from '@/features/media/hooks/useMediaPlaceholder';
import type { PostFormValues } from '@/features/post-editor/types/postForm';
import { getLocationDefinition } from '@/features/post-editor/utils/locationDefinition';
import { resolvePostType } from '@/features/post-editor/utils/resolvePostType';
import { toSpecialPostCustomAttributes } from '@/features/post-editor/utils/specialPostCustomAttributes';

type EditPostOptions = {
  postId: string;
  media: ReturnType<typeof useMediaPlaceholders>;
  onSuccess: (post: ClientPostChangeConfirmation) => void;
};

/**
 * Edit counterpart of {@link useCreatePost}: takes the shared `PostFormValues`
 * and dispatches to the matching `useChangeXxx` mutation. The channel (and
 * therefore the post type) is not changed in edit — the selector is locked —
 * so `resolvePostType(values)` is only used to pick the matching change hook.
 *
 * `useEmptyFields: true` is sent so the backend treats omitted fields as
 * "clear" rather than "leave as is" — matches the existing PostEditorScreen
 * behaviour.
 */
export const useEditPost = ({ postId, media, onSuccess }: EditPostOptions) => {
  const changeGossip = useChangeGossip();
  const changeHappening = useChangeHappening();
  const changeNewsItem = useChangeNewsItem();
  const changeOffer = useChangeOffer();
  const changeSeeking = useChangeSeeking();
  const changeSpecialPost = useChangeSpecialPost();
  const changeSuggestion = useChangeSuggestion();

  return useCallback(
    (values: PostFormValues) => {
      const postType = resolvePostType(values);
      const base = {
        postId,
        title: values.title?.trim() || undefined,
        text: values.text.trim(),
        documentItemPlaceHolders: media.documentItemPlaceholders,
        mediaItemPlaceHolders: media.mediaItemPlaceholders,
        useEmptyFields: true
      };

      switch (postType) {
        case ClientPostTypeEnum.NewsItem: {
          changeNewsItem.mutate(
            {
              ...base,
              categories: values.categories,
              geoAreaIds: values.geoAreaIds,
              hiddenForOtherHomeAreas: values.hiddenForOtherHomeAreas
            },
            { onSuccess }
          );
          return;
        }
        case ClientPostTypeEnum.Gossip: {
          changeGossip.mutate(base, { onSuccess });
          return;
        }
        case ClientPostTypeEnum.Happening: {
          changeHappening.mutate(
            {
              ...base,
              allDayEvent: values.allDay,
              startTime: values.startDate?.getTime(),
              endTime: values.endDate?.getTime(),
              customLocationDefinition: getLocationDefinition(values.location)
            },
            { onSuccess }
          );
          return;
        }
        case ClientPostTypeEnum.Offer: {
          changeOffer.mutate(base, { onSuccess });
          return;
        }
        case ClientPostTypeEnum.Seeking: {
          changeSeeking.mutate(base, { onSuccess });
          return;
        }
        case ClientPostTypeEnum.SpecialPost: {
          changeSpecialPost.mutate(
            {
              ...base,
              customAttributes: toSpecialPostCustomAttributes(
                values.dorfHelferType
              )
            },
            { onSuccess }
          );
          return;
        }
        case ClientPostTypeEnum.Suggestion: {
          changeSuggestion.mutate(
            {
              ...base,
              suggestionCategoryId: values.suggestionCategory?.id,
              customLocationDefinition: getLocationDefinition(values.location)
            },
            { onSuccess }
          );
          return;
        }
      }
    },
    [
      postId,
      media.documentItemPlaceholders,
      media.mediaItemPlaceholders,
      onSuccess,
      changeGossip,
      changeHappening,
      changeNewsItem,
      changeOffer,
      changeSeeking,
      changeSpecialPost,
      changeSuggestion
    ]
  );
};
